import {
  API_BASE_PATH,
  CSRF_COOKIE_NAME,
  CSRF_HEADER,
  apiErrorEnvelopeSchema,
  csrfTokenResponseSchema,
  currentUserSchema,
  type ApiErrorDetail,
  type ErrorCode,
} from '@mfos/shared';
import type { ZodType } from 'zod';

export type ClientErrorCode = ErrorCode | 'NETWORK_ERROR' | 'INVALID_RESPONSE';

export class ApiError extends Error {
  constructor(
    readonly code: ClientErrorCode,
    message: string,
    readonly status?: number,
    readonly requestId?: string,
    readonly details?: readonly ApiErrorDetail[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function isApiError(error: unknown, code?: ClientErrorCode): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiRequestOptions<T> {
  readonly method?: HttpMethod;
  /** Response schema; omit only for endpoints that answer 204 No Content. */
  readonly schema?: ZodType<T>;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Non-2xx statuses whose body still conforms to `schema` (e.g. readiness 503). */
  readonly acceptStatuses?: readonly number[];
  /**
   * When `false`, a failed session refresh only rejects the request instead of signing the user
   * out globally (used by public pages that merely probe for an existing session).
   */
  readonly notifySessionExpired?: boolean;
  /**
   * Extra request headers (e.g. `Idempotency-Key`). They are sent unchanged on every retry of the
   * same call, and cannot override the protocol headers (accept, content-type, CSRF).
   */
  readonly headers?: Readonly<Record<string, string>>;
}

export type ApiGetOptions<T> = Omit<ApiRequestOptions<T>, 'method' | 'body'> & {
  readonly schema: ZodType<T>;
};

const AUTH_PATH = `${API_BASE_PATH}/auth`;
const CSRF_PATH = `${AUTH_PATH}/csrf`;
const REFRESH_PATH = `${AUTH_PATH}/refresh`;
const REAUTHENTICATE_PATH = `${AUTH_PATH}/reauthenticate`;

/** Endpoints that never carry a session: a 401 from them is final and must not trigger refresh. */
const PUBLIC_PATHS: readonly string[] = [
  CSRF_PATH,
  `${AUTH_PATH}/login`,
  `${AUTH_PATH}/login/mfa`,
  `${AUTH_PATH}/magic-link`,
  `${AUTH_PATH}/magic-link/consume`,
  `${AUTH_PATH}/password/forgot`,
  `${AUTH_PATH}/password/reset`,
  REFRESH_PATH,
  `${API_BASE_PATH}/invitations/preview`,
  `${API_BASE_PATH}/invitations/accept`,
];

function isPublicPath(path: string): boolean {
  const pathname = path.split('?')[0] ?? path;
  return PUBLIC_PATHS.includes(pathname);
}

function isUnsafeMethod(method: HttpMethod): boolean {
  return method !== 'GET';
}

type ReauthenticationHandler = () => Promise<boolean>;
type Listener = () => void;

let reauthenticationHandler: ReauthenticationHandler | null = null;
const sessionExpiredListeners = new Set<Listener>();
const mfaEnrollmentListeners = new Set<Listener>();
let refreshInFlight: Promise<boolean> | null = null;
let csrfInFlight: Promise<string> | null = null;
let reauthInFlight: Promise<boolean> | null = null;

/**
 * Registers the UI that asks the user to confirm their identity (step-up). It resolves `true`
 * after a successful `POST /auth/reauthenticate`, `false` when the user cancels.
 */
export function setReauthenticationHandler(handler: ReauthenticationHandler | null): void {
  reauthenticationHandler = handler;
}

/** Called once a session can no longer be refreshed: the user is signed out. */
export function onSessionExpired(listener: Listener): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

/** Called when the workspace policy requires MFA enrollment before continuing. */
export function onMfaEnrollmentRequired(listener: Listener): () => void {
  mfaEnrollmentListeners.add(listener);
  return () => mfaEnrollmentListeners.delete(listener);
}

/** Test hook: forget in-flight single-flight promises and registered handlers. */
export function resetApiClientState(): void {
  reauthenticationHandler = null;
  sessionExpiredListeners.clear();
  mfaEnrollmentListeners.clear();
  refreshInFlight = null;
  csrfInFlight = null;
  reauthInFlight = null;
}

export function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const [rawKey, ...rest] = part.trim().split('=');
    if (rawKey === name) {
      const value = rest.join('=');
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    }
  }
  return null;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new ApiError(
      'INVALID_RESPONSE',
      'The server returned an unreadable response.',
      response.status,
    );
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  const body = await readJson(response).catch(() => undefined);
  const envelope = apiErrorEnvelopeSchema.safeParse(body);
  if (envelope.success) {
    const { code, message, requestId, details } = envelope.data.error;
    return new ApiError(code, message, response.status, requestId, details);
  }
  return new ApiError(
    'INVALID_RESPONSE',
    `Unexpected response (${String(response.status)}).`,
    response.status,
  );
}

const PROTOCOL_HEADERS: ReadonlySet<string> = new Set(['accept', 'content-type', CSRF_HEADER]);

async function send(
  path: string,
  method: HttpMethod,
  init: {
    body?: unknown;
    signal?: AbortSignal;
    csrfToken?: string | null;
    headers?: Readonly<Record<string, string>>;
  },
): Promise<Response> {
  const headers: Record<string, string> = {};
  for (const [name, value] of Object.entries(init.headers ?? {})) {
    const key = name.toLowerCase();
    if (!PROTOCOL_HEADERS.has(key)) {
      headers[key] = value;
    }
  }
  headers.accept = 'application/json';
  if (init.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (init.csrfToken !== undefined && init.csrfToken !== null) {
    headers[CSRF_HEADER] = init.csrfToken;
  }
  try {
    return await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
      ...(init.signal !== undefined ? { signal: init.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new ApiError('NETWORK_ERROR', 'The server could not be reached.');
  }
}

/** Fetches a fresh double-submit token; the server also (re)sets the readable cookie. */
export function fetchCsrfToken(): Promise<string> {
  csrfInFlight ??= (async () => {
    try {
      const response = await send(CSRF_PATH, 'GET', {});
      if (!response.ok) {
        throw await toApiError(response);
      }
      const parsed = csrfTokenResponseSchema.safeParse(await readJson(response));
      if (!parsed.success) {
        throw new ApiError(
          'INVALID_RESPONSE',
          'The server response did not match the expected format.',
          response.status,
        );
      }
      return readCookie(CSRF_COOKIE_NAME) ?? parsed.data.csrfToken;
    } finally {
      csrfInFlight = null;
    }
  })();
  return csrfInFlight;
}

async function csrfTokenForRequest(): Promise<string> {
  return readCookie(CSRF_COOKIE_NAME) ?? (await fetchCsrfToken());
}

/**
 * Rotates the session with the refresh cookie. Concurrent callers share one request so a rotating
 * refresh token is never presented twice.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      await apiRequest(REFRESH_PATH, {
        method: 'POST',
        schema: currentUserSchema,
        notifySessionExpired: false,
      });
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function requestReauthentication(handler: ReauthenticationHandler): Promise<boolean> {
  reauthInFlight ??= handler().finally(() => {
    reauthInFlight = null;
  });
  return reauthInFlight;
}

function notify(listeners: ReadonlySet<Listener>): void {
  for (const listener of [...listeners]) {
    listener();
  }
}

async function parseBody<T>(response: Response, schema: ZodType<T> | undefined): Promise<T> {
  if (schema === undefined) {
    if (response.status !== 204) {
      throw new ApiError(
        'INVALID_RESPONSE',
        'The server returned content where none was expected.',
        response.status,
      );
    }
    return undefined as T;
  }
  const parsed = schema.safeParse(await readJson(response));
  if (!parsed.success) {
    throw new ApiError(
      'INVALID_RESPONSE',
      'The server response did not match the expected format.',
      response.status,
    );
  }
  return parsed.data;
}

/**
 * Sends a JSON request to the API and validates the response at the boundary.
 *
 * Protocol: unsafe methods carry the double-submit CSRF token (retried once after
 * `CSRF_TOKEN_INVALID`); a 401 on a session endpoint triggers one shared refresh and one retry;
 * `REAUTHENTICATION_REQUIRED` on a mutation asks the registered handler for step-up and retries
 * once; `MFA_ENROLLMENT_REQUIRED` notifies listeners. Mutations are otherwise never replayed.
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions<T> = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const unsafe = isUnsafeMethod(method);
  const publicPath = isPublicPath(path);
  let csrfRetried = false;
  let refreshed = false;
  let refreshFailed = false;
  let reauthenticated = false;

  for (;;) {
    const csrfToken = unsafe ? await csrfTokenForRequest() : null;
    const response = await send(path, method, {
      body: options.body,
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
      ...(options.headers !== undefined ? { headers: options.headers } : {}),
      csrfToken,
    });

    if (response.ok || (options.acceptStatuses ?? []).includes(response.status)) {
      return parseBody(response, options.schema);
    }

    const error = await toApiError(response);

    if (error.code === 'CSRF_TOKEN_INVALID' && unsafe && !csrfRetried) {
      csrfRetried = true;
      await fetchCsrfToken();
      continue;
    }

    if (error.status === 401 && error.code === 'UNAUTHENTICATED' && !publicPath) {
      if (!refreshed) {
        refreshed = true;
        // Retry even when the refresh failed: another tab may have rotated the session
        // concurrently, and its cookies are shared with this one.
        refreshFailed = !(await refreshSession());
        continue;
      }
      if (refreshFailed && options.notifySessionExpired !== false) {
        notify(sessionExpiredListeners);
      }
      throw error;
    }

    if (
      error.code === 'REAUTHENTICATION_REQUIRED' &&
      unsafe &&
      !reauthenticated &&
      path !== REAUTHENTICATE_PATH &&
      reauthenticationHandler !== null
    ) {
      reauthenticated = true;
      if (await requestReauthentication(reauthenticationHandler)) {
        continue;
      }
      throw new ApiError(
        'REAUTHENTICATION_REQUIRED',
        'Identity confirmation was cancelled, so the action was not performed.',
        error.status,
        error.requestId,
      );
    }

    if (error.code === 'MFA_ENROLLMENT_REQUIRED') {
      notify(mfaEnrollmentListeners);
    }
    throw error;
  }
}

/** GET a JSON resource and validate it at the boundary. Server responses are untrusted input. */
export function apiGet<T>(path: string, options: ApiGetOptions<T>): Promise<T> {
  return apiRequest(path, { ...options, method: 'GET' });
}

export function apiPost<T>(
  path: string,
  body: unknown,
  schema: ZodType<T>,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest(path, {
    method: 'POST',
    body,
    schema,
    ...(signal !== undefined ? { signal } : {}),
  });
}

/** POST with extra headers, e.g. the `Idempotency-Key` of a binding action. */
export function apiPostWithHeaders<T>(
  path: string,
  body: unknown,
  schema: ZodType<T>,
  headers: Readonly<Record<string, string>>,
): Promise<T> {
  return apiRequest(path, { method: 'POST', body, schema, headers });
}

/** POST to an endpoint that answers 204 No Content. */
export function apiPostNoContent(path: string, body?: unknown): Promise<void> {
  return apiRequest<undefined>(path, { method: 'POST', body }).then(() => undefined);
}

export function apiPatch<T>(path: string, body: unknown, schema: ZodType<T>): Promise<T> {
  return apiRequest(path, { method: 'PATCH', body, schema });
}

export function apiPut<T>(path: string, body: unknown, schema: ZodType<T>): Promise<T> {
  return apiRequest(path, { method: 'PUT', body, schema });
}

export function apiDelete<T>(path: string, schema: ZodType<T>): Promise<T> {
  return apiRequest(path, { method: 'DELETE', schema });
}

/** Builds `/api/v1/...` paths, encoding each dynamic segment. */
export function apiPath(...segments: readonly string[]): string {
  return `${API_BASE_PATH}/${segments.map((segment) => encodeURIComponent(segment)).join('/')}`;
}

export function withQuery(
  path: string,
  params: Readonly<Record<string, string | number | undefined>>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query === '' ? path : `${path}?${query}`;
}
